import slugify from 'slugify';
import { butterfly } from '$lib/config';
import type { ButterflyMedia } from '$lib/types';

type MediaUrlHandler = (
  media: ButterflyMedia,
  input: {
    format?: 'source' | 'default' | 'thumb' | 'square' | 'cover' | 'stories';
    ext?: 'jpg' | 'webp' | 'png' | 'mp4' | 'mp3' | 'gif';
    width?: number;
    definition?: 'hd' | 'sd';
    slug?: string;
  },
) => string;

const getMediaUrl: MediaUrlHandler = (
  media,
  { format = 'default', ext = 'webp', width = 0, definition = 'hd', slug = 'media' },
) => {
  const fingerprint = media.attributes.fingerprints[format];
  const $slug = slug
    ? slug
    : slugify(media.attributes.name, { lower: true, remove: /[*+~.()'"#!:@,?®^\\/]/g });

  if (['mp4', 'mp3'].indexOf(ext) >= 0) {
    if (['source', 'default'].indexOf(format) <= 0) {
      throw new Error(
        `[getMediaUrl] Invalid format for extension '${ext}', must be 'default' or 'source'.`,
      );
    }
    if (ext === 'mp4' && media.type !== 'video') {
      throw new Error(`[getMediaUrl] mp4 can only work on video, got '${media.attributes.type}'.`);
    }
    if (ext === 'mp3' && media.type !== 'audio') {
      throw new Error(`[getMediaUrl] mp3 can only work on audio, got '${media.attributes.type}'.`);
    }

    if (ext === 'mp3') {
      return `${butterfly.mediaUrl}/v1/${media.id}-${format}/${fingerprint}/${$slug}.${ext}`;
    }
    return `${butterfly.mediaUrl}/v1/${media.id}-${format}-${definition}/${fingerprint}/${$slug}.${ext}`;
  } else if (ext === 'gif' && format !== 'source') {
    throw new Error(`[getMediaUrl] Invalid format for extension 'gif', must be 'source'.`);
  } else if (format === 'source' && width > 0) {
    throw new Error('Can not change width of media source.');
  }

  if (width === 0) {
    return `${butterfly.mediaUrl}/v1/${media.id}-${format}/${fingerprint}/${$slug}.${ext}`;
  }
  const w = width > media.attributes.width ? media.attributes.width : width;
  return `${butterfly.mediaUrl}/v1/${media.id}-${format}-${w}/${fingerprint}/${$slug}.${ext}`;
};

export default getMediaUrl;
